import {
    fetchWinBackOffersReport,
    setSorting,
    WinBackOffersReportData,
} from 'features/reports/winBackOffersReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useCurrentCountry } from 'hooks/miscHooks'
import { useCallback, useMemo } from 'react'
import ChurnDetailsCell from './common/ChurnDetailsCell'
import ReportList, { ColumnDefinition } from './common/ReportList'
import {
    renderBoolean,
    renderDate,
    renderProspectValue,
    renderProviderId,
    renderProviderStatus,
    renderSubscriptionStatus,
    renderText,
} from './common/reportRenderers'

const WinBackOffersReportList = () => {
    const dispatch = useAppDispatch()
    const country = useCurrentCountry()

    // Select data from Redux store
    const { pages, rows, pagination, filters, sorting, isLoading } = useAppSelector(
        (state) => state.winBackOffersReport,
    )

    // Memoized columns definition
    const columns = useMemo(
        (): ColumnDefinition[] => [
            { key: 'provider_id', name: 'Provider ID' },
            { key: 'provider_status', name: 'Provider Status' },
            { key: 'subscription_status', name: 'Subscription Status' },
            { key: 'subscription_start_date', name: 'Subscription Start Date' },
            { key: 'branch', name: 'Branch' },
            {
                key: 'first_invoice_subscription_month',
                name: 'First Invoice Month',
                sortable: false,
            },
            { key: 'prospect_value', name: 'Prospect Value', sortable: false },
            { key: 'win_back_offer_seen', name: 'Win-Back Offer Seen' },
            { key: 'win_back_offer_variant', name: 'Win-Back Offer Variant', sortable: false },
            { key: 'win_back_offer_deal', name: 'Win-Back Offer Deal', sortable: false },
            { key: 'win_back_offer_accepted', name: 'Win-Back Offer Accepted', sortable: false },
            { key: 'win_back_shown_date', name: 'Shown Date' },
            { key: 'churn_details', name: 'Churn Details', sortable: false },
        ],
        [],
    )

    // Memoized row renderer
    const renderRow = useCallback(
        (id: string) => {
            const item = rows[id] as unknown as WinBackOffersReportData
            if (!item) {
                return { cols: [] }
            }

            return {
                cols: [
                    renderProviderId(
                        item.provider_id,
                        'provider_id',
                        '/service/providers/provider',
                    ),
                    renderProviderStatus(item.provider_status, 'provider_status'),
                    renderSubscriptionStatus(item.subscription_status, 'subscription_status'),
                    renderDate(item.subscription_start_date, 'subscription_start_date'),
                    renderText(item.branch, 'branch'),
                    renderText(
                        item.first_invoice_subscription_month,
                        'first_invoice_subscription_month',
                    ),
                    renderProspectValue(item.prospect_value, 'prospect_value'),
                    renderBoolean(item.win_back_offer_seen, 'win_back_offer_seen'),
                    renderText(item.win_back_offer_variant, 'win_back_offer_variant', '-'),
                    renderText(item.win_back_offer_deal, 'win_back_offer_deal', '-'),
                    renderBoolean(item.win_back_offer_accepted, 'win_back_offer_accepted'),
                    renderDate(item.win_back_shown_date, 'win_back_shown_date'),
                    <ChurnDetailsCell
                        key="churn_details"
                        reason={item.churn_reason}
                        comment={item.churn_comment}
                    />,
                ],
            }
        },
        [rows],
    )

    // Handle page change
    const handlePageChange = useCallback(
        (page: number) => {
            dispatch(fetchWinBackOffersReport({ filters: filters, page, sorting, country }))
        },
        [dispatch, filters, sorting, country],
    )

    // Handle sort change
    const handleSortChange = useCallback(
        (field: string, direction: 'ASC' | 'DESC') => {
            dispatch(setSorting({ field, direction }))
            dispatch(
                fetchWinBackOffersReport({
                    filters,
                    page: 0,
                    sorting: { field, direction },
                    country,
                }),
            )
        },
        [dispatch, filters, country],
    )

    return (
        <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-xl font-bold text-grey-900">Offers</h2>
                <p className="text-md text-grey-700 mb-1">
                    One row per offer shown in the selected period.
                </p>
            </div>
            <ReportList
                pages={pages}
                rows={rows}
                isLoading={isLoading}
                pagination={pagination}
                filters={filters}
                fetchData={handlePageChange}
                columns={columns}
                renderRow={renderRow}
                sorting={sorting}
                onSortChange={handleSortChange}
                emptyMessage="No win-back offers found matching the current filters."
                loadingMessage="Loading win-back offers data..."
            />
        </section>
    )
}

export default WinBackOffersReportList
